/**
 * Startup check: is the API we are talking to one this build was written for?
 *
 * Reads `GET /api/v1/meta/compat` (falling back to `/openapi.json`'s `info.version` for an API
 * that predates the endpoint) and compares both directions: the served API against our
 * `MIN_API_VERSION`, and the API's `min_frontend_version` against our own version. A mismatch is
 * logged with `console.error` and returned as a message for a non-blocking admin banner. This is
 * a diagnostic, never a gate: every failure path swallows the error and reports nothing.
 */
import { useEffect, useState } from 'react';

import { MIN_API_VERSION } from '../api-compat';
import type { components } from '../types/api.generated';
import { resolveApiUrl } from '../utils/fetcher';
import { compareSemver, isSemver } from '../utils/semver';
import { VERSION } from '../utils/version';

export type CompatStatement = components['schemas']['CompatResponse'];

export interface ApiCompatResult {
  /** Set only when a check ran and found a mismatch. */
  mismatch: string | null;
  /** What the API reported, when it could be read. */
  api: CompatStatement | null;
}

/** Pure comparison, exported for tests. `null` means compatible. */
export const describeMismatch = (
  api: CompatStatement,
  ours: { version: string; minApiVersion: string } = { version: VERSION, minApiVersion: MIN_API_VERSION }
): string | null => {
  const problems: string[] = [];
  if (isSemver(api.api_version) && isSemver(ours.minApiVersion) && compareSemver(api.api_version, ours.minApiVersion) < 0) {
    problems.push(`the API is ${api.api_version} but this build needs at least ${ours.minApiVersion}`);
  }
  if (isSemver(api.min_frontend_version) && isSemver(ours.version) && compareSemver(ours.version, api.min_frontend_version) < 0) {
    problems.push(`the API requires frontend ${api.min_frontend_version} or newer but this build is ${ours.version}`);
  }
  if (problems.length === 0) return null;
  return `Frontend ${ours.version} may not be compatible with this API: ${problems.join('; ')}.`;
};

const readCompat = async (): Promise<CompatStatement | null> => {
  const response = await fetch(resolveApiUrl('/api/v1/meta/compat'));
  if (response.ok) {
    const body = (await response.json()) as Partial<CompatStatement>;
    if (typeof body.api_version === 'string' && typeof body.min_frontend_version === 'string') {
      return { api_version: body.api_version, min_frontend_version: body.min_frontend_version };
    }
    return null;
  }
  if (response.status !== 404) return null;
  // An API from before the compat endpoint: its version is still in the OpenAPI document, and an
  // API that old cannot state a minimum frontend, so treat it as accepting any.
  const spec = await fetch(resolveApiUrl('/openapi.json'));
  if (!spec.ok) return null;
  const body = (await spec.json()) as { info?: { version?: string } };
  const version = body.info?.version;
  return typeof version === 'string' ? { api_version: version, min_frontend_version: '0.0.0' } : null;
};

export const useApiCompat = (): ApiCompatResult => {
  const [result, setResult] = useState<ApiCompatResult>({ mismatch: null, api: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = await readCompat();
        if (cancelled || !api) return;
        const mismatch = describeMismatch(api);
        if (mismatch) console.error(`API compatibility: ${mismatch}`);
        setResult({ mismatch, api });
      } catch {
        // Unreachable API, relative URL in a non-browser runtime, malformed body: say nothing.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return result;
};
